import {
  type ProjectDetail,
  type TaskDetail,
  type TaskStatus,
  taskCommentBodySchema,
} from "@esi/shared";
import { MessageSquare, Plus, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Markdown } from "@/components/markdown/Markdown";
import { InlineError, Notice } from "@/components/Notice";
import { SectionHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { ApiError } from "../api-errors.ts";
import { focusComposerOnOpen } from "../conversation/composer-focus.ts";
import {
  Link,
  navigate,
  PROJECTS_PATH,
  projectConversationPath,
  projectHomePath,
} from "../router.tsx";
import { useReloadHold } from "../shell/build-refresh.ts";
import {
  DueDate,
  sourcePath,
  TaskError,
  TaskHeading,
  TaskSelect,
  TaskStatusText,
  textAreaClass,
  writeError,
} from "./parts.tsx";
import { denied, useStartTaskConversation, useTask, useTaskWrite } from "./queries.ts";
import { TaskEditor } from "./TaskEditor.tsx";
import { TaskLinkPicker } from "./TaskLinkPicker.tsx";

export function TaskDetailPage({ project, taskId }: { project: ProjectDetail; taskId: string }) {
  const { t } = useTranslation();
  const query = useTask(project.id, taskId);
  const task = denied(query.error) ? null : query.data;
  useDocumentTitle(
    task ? (task.hidden ? t("tasks.restricted") : task.title) : t("tasks.unavailable"),
  );
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <nav
        aria-label={t("tasks.backTasks")}
        className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-fg-secondary"
      >
        <Link href={PROJECTS_PATH} className="hover:underline">
          {t("nav.projects")}
        </Link>
        <span aria-hidden="true">/</span>
        <Link
          data-esi-content="projects"
          href={projectHomePath(project.id)}
          className="break-words hover:underline"
        >
          {project.name}
        </Link>
        <span aria-hidden="true">/</span>
        <Link href={projectHomePath(project.id, "tasks")} className="hover:underline">
          {t("tasks.title")}
        </Link>
      </nav>
      {query.isPending ? (
        <div
          role="status"
          className="h-40 animate-pulse rounded-lg bg-surface-sunken motion-reduce:animate-none"
          aria-label={t("app.loading")}
        />
      ) : !task ? (
        <>
          <TaskHeading>{t("tasks.unavailable")}</TaskHeading>
          <Notice>{t("tasks.unavailableBody")}</Notice>
          {query.isError && !denied(query.error) ? (
            <TaskError error={query.error} retry={() => void query.refetch()} />
          ) : null}
          <Button variant="outline" asChild>
            <Link href={projectHomePath(project.id, "tasks")}>{t("tasks.backTasks")}</Link>
          </Button>
        </>
      ) : (
        <>
          {query.isError ? (
            <>
              <TaskError error={query.error} retry={() => void query.refetch()} />
              <Notice>{t("tasks.stale")}</Notice>
            </>
          ) : null}
          <Detail
            key={`${task.id}:${task.hidden}`}
            project={project}
            task={task}
            refresh={() => void query.refetch()}
          />
        </>
      )}
    </div>
  );
}
function Detail({
  project,
  task,
  refresh,
}: {
  project: ProjectDetail;
  task: TaskDetail;
  refresh: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [editor, setEditor] = useState(false);
  const [source, setSource] = useState(false);
  const [deleteTask, setDeleteTask] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const write = useTaskWrite(project.id, task.id);
  const start = useStartTaskConversation(project.id, task.id);
  const [startError, setStartError] = useState<string | null>(null);
  const actions = task.actions;
  const [status, setStatus] = useState<TaskStatus>(task.status);
  useEffect(() => {
    setStatus(task.status);
  }, [task.status]);
  useReloadHold(write.isPending || (Boolean(actions?.changeStatus) && status !== task.status));
  const run = async (input: Parameters<typeof write.mutateAsync>[0]) => {
    setError(null);
    try {
      await write.mutateAsync(input);
      return true;
    } catch (e) {
      setError(writeError(e, t));
      return false;
    }
  };
  const stamp = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );
  const canEdit = actions && (actions.editText || actions.editSchedule || actions.assign);
  // Work on this with ESI: one POST, then the new conversation with its composer focused. A
  // refusal keeps the user here with its reason; the detail refreshes, so a frozen project or
  // tasks turned off remove the action. Never retried automatically.
  const startConversation = async () => {
    if (start.isPending) {
      return;
    }
    setStartError(null);
    try {
      const conversation = await start.mutateAsync();
      focusComposerOnOpen(conversation.id);
      navigate(projectConversationPath(project.id, conversation.id));
    } catch (e) {
      setStartError(
        e instanceof ApiError && e.status < 500 ? e.message : t("tasks.startUncertain"),
      );
    }
  };
  // Known default labels are localized; custom server classification IDs remain honest text.
  const levels = {
    public: t("tasks.levelPublic"),
    internal: t("tasks.levelInternal"),
    confidential: t("tasks.levelConfidential"),
    restricted: t("audience.restricted"),
  };
  return (
    <>
      <div data-esi-content="tasks" className="flex flex-wrap items-start justify-between gap-3">
        <TaskHeading>{task.hidden ? t("tasks.restricted") : task.title}</TaskHeading>
        <div className="flex flex-wrap gap-2">
          {actions?.startConversation ? (
            <Button
              size="sm"
              disabled={start.isPending}
              aria-busy={start.isPending || undefined}
              onClick={() => void startConversation()}
            >
              <SquarePen aria-hidden="true" />
              {start.isPending ? t("tasks.starting") : t("tasks.startConversation")}
            </Button>
          ) : null}
          {canEdit ? (
            <Button size="sm" variant="outline" onClick={() => setEditor(true)}>
              {t("tasks.edit")}
            </Button>
          ) : null}
          {actions?.delete ? (
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={write.isPending}
              aria-label={t("tasks.delete")}
              onClick={() => setDeleteTask(true)}
            >
              <Trash2 aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      </div>
      {startError ? <InlineError>{startError}</InlineError> : null}
      {!actions ? (
        <Notice
          action={
            <Button size="sm" variant="outline" onClick={refresh}>
              {t("tasks.refresh")}
            </Button>
          }
        >
          {t("tasks.metadata")}
        </Notice>
      ) : actions.writeBlock ? (
        <Notice>
          {t(actions.writeBlock === "project_frozen" ? "tasks.frozen" : "tasks.disabled")}
        </Notice>
      ) : null}
      <div
        data-esi-content="tasks"
        className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-line pb-5"
      >
        <TaskStatusText status={task.status} />
        <span className="text-sm text-fg-secondary">
          {t("tasks.assignee")}:{" "}
          {task.assignee ? (
            <span data-esi-content="people">
              {task.assignee.name ?? t("tasks.unavailablePerson")}
            </span>
          ) : (
            t("tasks.unassigned")
          )}
        </span>
        <DueDate due={task.dueDate} status={task.status} />
      </div>
      {actions?.changeStatus ? (
        <div className="flex flex-wrap items-end gap-3">
          <TaskSelect
            label={t("tasks.status")}
            value={status}
            onChange={setStatus}
            disabled={write.isPending}
            options={(["todo", "in_progress", "blocked", "done"] as const).map((value) => ({
              value,
              label: t(`tasks.${value}`),
            }))}
          />
          <Button
            variant="outline"
            disabled={write.isPending || status === task.status}
            onClick={() => {
              if (!write.isPending && status !== task.status) {
                void run({ kind: "edit", data: { status } });
              }
            }}
          >
            {write.isPending ? t("app.saving") : t("tasks.applyStatus")}
          </Button>
        </div>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {!task.hidden ? (
        <>
          <div className="flex flex-col gap-1 text-xs text-fg-muted">
            <p>
              {t("tasks.classification", {
                label: levels[task.classification as keyof typeof levels] ?? task.classification,
              })}
            </p>
            <p data-esi-content="people">
              {t("tasks.creator", { name: task.createdBy.name ?? t("tasks.unavailablePerson") })}
            </p>
            <p>{t("tasks.createdAt", { time: stamp(task.createdAt) })}</p>
            <p>{t("tasks.updatedAt", { time: stamp(task.updatedAt) })}</p>
            {task.completedAt ? (
              <p>{t("tasks.completedAt", { time: stamp(task.completedAt) })}</p>
            ) : null}
          </div>
          {task.description ? (
            <section
              data-esi-content="tasks"
              aria-label={t("tasks.description")}
              className="max-w-prose break-words"
            >
              <Markdown markdown={task.description} headingBase={2} showLinkHost />
            </section>
          ) : null}
          <section aria-labelledby="task-sources" className="flex flex-col gap-3">
            <SectionHeader
              id="task-sources"
              title={t("tasks.sources")}
              actions={
                actions?.manageLinks ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={write.isPending || task.links.length >= 20}
                    onClick={() => setSource(true)}
                  >
                    <Plus aria-hidden="true" />
                    {t("tasks.addSource")}
                  </Button>
                ) : undefined
              }
            />
            {task.links.length === 0 ? (
              <p className="text-sm text-fg-muted">{t("tasks.noSources")}</p>
            ) : (
              <ul
                data-esi-content="tasks"
                className="divide-y divide-line rounded-lg border border-line bg-surface-raised"
              >
                {task.links.map((link) => {
                  const href = sourcePath(project.id, link);
                  return (
                    <li key={link.id} className="flex items-center gap-3 px-3 py-3">
                      <div className="min-w-0 flex-1 text-sm break-words">
                        {href && link.available ? (
                          <Link
                            href={href}
                            className="text-accent-text underline underline-offset-4"
                          >
                            {link.title ?? t(`tasks.${link.kind}`)}
                          </Link>
                        ) : (
                          t("tasks.unavailableSource")
                        )}
                      </div>
                      {actions?.manageLinks ? (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          disabled={write.isPending}
                          aria-label={t("tasks.removeSource")}
                          onClick={() => void run({ kind: "deleteLink", id: link.id })}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {task.links.length >= 20 && actions?.manageLinks ? (
              <p className="text-xs text-fg-muted">{t("tasks.linkLimit")}</p>
            ) : null}
          </section>
          <Comments projectId={project.id} task={task} />
        </>
      ) : null}
      {editor && canEdit ? (
        <TaskEditor projectId={project.id} task={task} onClose={() => setEditor(false)} />
      ) : null}
      {source && !task.hidden && actions?.manageLinks ? (
        <TaskLinkPicker projectId={project.id} taskId={task.id} onClose={() => setSource(false)} />
      ) : null}
      {deleteTask && actions?.delete ? (
        <AppDialog
          open
          onClose={() => {
            if (!write.isPending) {
              setDeleteTask(false);
            }
          }}
          title={t("tasks.delete")}
          description={t("tasks.deleteBody")}
        >
          <DialogFooter>
            <Button
              variant="outline"
              disabled={write.isPending}
              onClick={() => setDeleteTask(false)}
            >
              {t("app.cancel")}
            </Button>
            <Button
              variant="destructive"
              className="text-accent-on dark:bg-destructive dark:hover:bg-destructive/90"
              disabled={write.isPending}
              onClick={async () => {
                if (await run({ kind: "delete" })) {
                  navigate(projectHomePath(project.id, "tasks"));
                }
              }}
            >
              {t("tasks.delete")}
            </Button>
          </DialogFooter>
          {error ? <InlineError>{error}</InlineError> : null}
        </AppDialog>
      ) : null}
    </>
  );
}
function Comments({
  projectId,
  task,
}: {
  projectId: string;
  task: Extract<TaskDetail, { hidden: false }>;
}) {
  const { t, i18n } = useTranslation();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [remove, setRemove] = useState<string | null>(null);
  const write = useTaskWrite(projectId, task.id);
  useReloadHold(body.length > 0 || write.isPending);
  const submit = async () => {
    const parsed = taskCommentBodySchema.safeParse(body);
    if (!parsed.success) {
      setError(t("tasks.commentInvalid"));
      return;
    }
    setError(null);
    try {
      await write.mutateAsync({ kind: "comment", body: parsed.data });
      setBody("");
    } catch (e) {
      setError(writeError(e, t));
    }
  };
  return (
    <section aria-labelledby="task-comments" className="flex flex-col gap-4">
      <SectionHeader id="task-comments" title={t("tasks.comments")} />
      {task.comments.length ? (
        <ul
          data-esi-content="tasks"
          className="divide-y divide-line rounded-lg border border-line bg-surface-raised"
        >
          {task.comments.map((comment) => (
            <li key={comment.id} className="flex flex-col gap-2 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 text-sm text-fg-secondary">
                  <span data-esi-content="people" className="break-words font-medium">
                    {comment.author.name ?? t("tasks.unavailablePerson")}
                  </span>
                  <time className="ml-3 text-xs text-fg-muted" dateTime={comment.createdAt}>
                    {new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(comment.createdAt))}
                  </time>
                </div>
                {comment.canDelete ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={write.isPending}
                    aria-label={t("tasks.deleteComment")}
                    onClick={() => setRemove(comment.id)}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
              <p className="text-sm whitespace-pre-wrap break-words text-fg">{comment.body}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-muted">{t("tasks.noComments")}</p>
      )}
      {task.actions?.comment ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!write.isPending) {
              void submit();
            }
          }}
        >
          <label htmlFor="task-comment" className="text-sm font-medium">
            {t("tasks.commentText")}
          </label>
          <textarea
            id="task-comment"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className={textAreaClass}
            disabled={write.isPending}
            aria-describedby="task-comment-count"
          />
          <p id="task-comment-count" className="text-xs text-fg-muted tabular-nums">
            {Array.from(body.normalize("NFC").trim()).length}/2000
          </p>
          <div>
            <Button type="submit" disabled={write.isPending}>
              <MessageSquare aria-hidden="true" />
              {write.isPending ? t("app.saving") : t("tasks.comment")}
            </Button>
          </div>
        </form>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {remove && task.comments.some((c) => c.id === remove && c.canDelete) ? (
        <AppDialog
          open
          onClose={() => {
            if (!write.isPending) {
              setRemove(null);
            }
          }}
          title={t("tasks.deleteComment")}
          description={t("tasks.removeCommentBody")}
        >
          <DialogFooter>
            <Button variant="outline" disabled={write.isPending} onClick={() => setRemove(null)}>
              {t("app.cancel")}
            </Button>
            <Button
              variant="destructive"
              className="text-accent-on dark:bg-destructive dark:hover:bg-destructive/90"
              disabled={write.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await write.mutateAsync({ kind: "deleteComment", id: remove });
                  setRemove(null);
                } catch (e) {
                  setError(writeError(e, t));
                }
              }}
            >
              {t("tasks.deleteComment")}
            </Button>
          </DialogFooter>
          {error ? <InlineError>{error}</InlineError> : null}
        </AppDialog>
      ) : null}
    </section>
  );
}
